import React, { Fragment } from 'react';
import L from 'leaflet';
import WorkTime from '../static/images/WorkTime.svg';
import Avatar from '@material-ui/core/Avatar';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';

import {
    Done,
    Error,
    Warning,
    Sync,
    SwapHoriz,
    CallSplit,
    ArrowRightAlt,
    Lock,
    KeyboardArrowUp,
    KeyboardArrowDown
} from '@material-ui/icons';
import { green, red, grey, blue } from '@material-ui/core/colors';
import { Typography } from '@material-ui/core';

export function date(value) {
    return new Date(value).toLocaleString();
}

export function localeDate(value) {
    return new Date(value).toLocaleDateString();
}

export function durationMillis(value) {
    if (value === undefined) {
        return '-';
    }
    const ms = value % 1000;
    value = Math.floor(value / 1000);
    const s = value % 60;
    value = Math.floor(value / 60);
    const m = value % 60;
    value = Math.floor(value / 60);
    let res = [];
    if (value > 0) res.push(`${value}h`);
    if (m > 0) res.push(`${m}m`);
    if (s > 0) res.push(`${s}s`);
    if (ms > 0) res.push(`${ms}ms`);
    return res.join(' ');
}

export function duration(value) {
    if (value === undefined) {
        return '-';
    }
    if (value === 0) {
        return '0';
    }
    const s = value % 60;
    value = Math.floor(value / 60);
    const m = value % 60;
    value = Math.floor(value / 60);
    let res = [];
    if (value > 0) res.push(`${value}h`);
    if (m > 0) res.push(`${m}m`);
    if (s > 0) res.push(`${Math.floor(s)}s`);
    return res.join(' ');
}

export function statusColor(value, test) {
    if (test) {
        return grey[500];
    }
    switch (value) {
    case 'success':
        return green[500];
    case 'failed':
        return red[500];
    case 'in progress':
    case 'in_progress':
        return blue[500];
    default:
        return '#000000';
    }
}

export function status(value, test) {
    const style = {
        color: '#fff',
        width: 20,
        height: 20
    };
    const title = `Status: ${value}`;
    if (value === 'success') {
        return (
            <Tooltip title={title}>
                <Avatar
                    style={{ ...style, backgroundColor: statusColor(value, test) }}
                    component="span"
                >
                    <Done style={{ width: '18px', height: '18px' }} />
                </Avatar>
            </Tooltip>
        );
    } else if (value === 'failed') {
        return (
            <Tooltip title={title}>
                <Avatar style={{ ...style, backgroundColor: statusColor(value, test) }}>
                    <Error style={{ width: '18px', height: '18px' }} />
                </Avatar>
            </Tooltip>
        );
    } else if (value === 'in progress') {
        return (
            <Tooltip title={title}>
                <Avatar style={{ ...style, backgroundColor: statusColor(value, test) }}>
                    <Sync style={{ width: '18px', height: '18px' }} />
                </Avatar>
            </Tooltip>
        );
    }
    return value;
}

export function coordinate(value) {
    if (value) {
        return `${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}`;
    }
    return '-';
}

export function timestampToHoursSeconds(value) {
    return (
        twoDigits(new Date(value).getHours()) +
    ':' +
    twoDigits(new Date(value).getMinutes())
    );
}

function twoDigits(value) {
    if (value < 10) {
        return '0' + value;
    }
    return value;
}

export function timeSeconds(value) {
    if (value !== undefined) {
        const seconds = value % 60;
        value = Math.floor(value / 60);
        const minutes = value % 60;
        const hours = Math.floor(value / 60) % 24;
        return `${twoDigits(hours)}:${twoDigits(minutes)}:${twoDigits(seconds)}`;
    }
    return '-';
}

export function timeWindows(value) {
    if (value !== undefined) {
        value = Math.floor(value / 60);
        const minutes = value % 60;
        const hours = Math.floor(value / 60) % 24;
        return `${twoDigits(hours)}:${twoDigits(minutes)}`;
    }
    return '-';
}

export function distance(value) {
    if (value === undefined) {
        return '-';
    }
    if (value < 1000) {
        return `${value}m`;
    }
    return `${Math.floor(value / 100).toFixed(1) / 10}km`;
}

export function size(value) {
    if (value === undefined) {
        return '-';
    }

    return value.reduce((element, index) => {
        if (index === value.length) {
            return element;
        }
        return `${element} -`;
    });
}

export function sizesWithPercentages(docs) {
    const { sizes, percentage } = docs;
    if (!sizes && !percentage) {
        return '';
    }
    const stringSizes =
    sizes === undefined || percentage === undefined
        ? '-'
        : sizes.reduce((prev, next, index) => {
            if (index === sizes.length - 1) {
                return `${prev}${next} (${percentage[index]}%)`;
            }
            return `${prev}${next} (${percentage[index]}%), `;
        }, '');

    return `${stringSizes}`;
}

export function difference(docs) {
    if (docs.differences === undefined) {
        return '';
    }
    const stringChanges =
    docs.differences.valor === undefined
        ? '-'
        : docs.differences.valor.reduce((prev, next, index) => {
            if (index === docs.differences.valor.length - 1) {
                return `${prev}${next}`;
            }
            return `${prev}${next}, `;
        }, '');
    if (docs.differences.type === 'plus') {
        return (
            <div style={{ verticalAlign: 'middle' }}>
                <Typography style={{ float: 'left' }}>{stringChanges}</Typography>
                <KeyboardArrowUp
                    style={{ color: green[500], height: '21px', float: 'left' }}
                />{' '}
            </div>
        );
    }
    if (docs.differences.type === 'less') {
        return (
            <div style={{ verticalAlign: 'middle' }}>
                <Typography style={{ float: 'left' }}>{stringChanges}</Typography>{' '}
                <KeyboardArrowDown
                    style={{ color: red[500], height: '21px', float: 'left' }}
                />
            </div>
        );
    }
    return `${stringChanges}`;
}

export function stopTime(doc) {
    if (!doc.arrival) {
        return `${timeWindows(doc.departure)}`;
    }
    if (!doc.departure) {
        return `${timeWindows(doc.arrival)}`;
    }
    return `${timeWindows(doc.arrival)} - ${timeWindows(doc.departure)}`;
}

export function list(values) {
    if (values === undefined || values.length === 0) {
        return '-';
    }
    return values.map(value => `${value}`).join(', ');
}

export function quotedList(values) {
    if (values === undefined || values.length === 0) {
        return '-';
    }
    return values.map(value => `'${value}'`).join(', ');
}

export function timewindow(value) {
    if (value === undefined || value.length !== 2) {
        return '-';
    }
    return `${timeSeconds(value[0])} - ${timeSeconds(value[1])}`;
}

export function timewindows(values) {
    if (values === undefined) {
        return '-';
    }
    return values.map(timewindow).join(', ');
}

export function arrayTimewindowsToString(array) {
    if (array === undefined) {
        return '-';
    }
    return array.map((tws, i) =>
        i !== array.length - 1
            ? timeWindows(tws[0]) + '-' + timeWindows(tws[1]) + ', '
            : timeWindows(tws[0]) + '-' + timeWindows(tws[1])
    );
}

export function timewindowWithTooltip(array) {
    return icon(WorkTime, arrayTimewindowsToString(array), 20);
}

export function metadata(meta) {
    if (meta === undefined) {
        return '-';
    }
    const keys = Object.keys(meta);
    if (keys.length === 0) {
        return '-';
    }
    const res = [];
    keys.forEach(key => res.push(`${key}: ${meta[key]}`));
    return res.join(', ');
}

export function key(value) {
    if (key === undefined || key.length === 0) {
        return '-';
    }
    return value.substring(0, 10) + '...';
}

export function tags(values) {
    if (key === undefined || key.length === 0) {
        return '-';
    }
    return (
        <Fragment>
            {values.map((tag, index) => (
                <Chip
                    component="span"
                    key={index}
                    label={tag}
                    style={{ margin: '1px', marginLeft: index === 0 ? '20px' : '4px' }}
                />
            ))}
        </Fragment>
    );
}

export function requestKind(value, size) {
    const title = `Method: ${value}`;
    if (value === 'synchronous') {
        return (
            <Tooltip title={title} style={size && { height: size }}>
                <ArrowRightAlt />
            </Tooltip>
        );
    } else if (value === 'asynchronous') {
        return (
            <Tooltip title={title} style={size && { height: size }}>
                <CallSplit />
            </Tooltip>
        );
    } else if (value === 'callback') {
        return (
            <Tooltip title={title} style={size && { height: size }}>
                <SwapHoriz />
            </Tooltip>
        );
    }
    return value;
}

export function comparison(operator) {
    if (operator === 'eq') {
        return '=';
    } else if (operator === 'lte') {
        return '<=';
    } else if (operator === 'lt') {
        return '<';
    } else if (operator === 'gte') {
        return '>=';
    } else if (operator === 'gt') {
        return '>';
    } else if (operator === 'neq') {
        return '<>';
    }
}

export function number(value) {
    if (value !== undefined) {
        return value;
    }
    return '-';
}

export function boolean(value) {
    if (value === true) {
        return 'True';
    } else if (value === false) {
        return 'False';
    }
    return '-';
}

export function booleanLockIcon(locked) {
    if (locked === true) {
        return <Lock color="secondary" />;
    }
}

export function capitalize(s) {
    return s && s[0].toUpperCase() + s.slice(1);
}

export function icon(Icon, tooltip, size = 20) {
    if (tooltip) {
        return (
            <Tooltip title={tooltip}>
                {typeof Icon === 'string' ? (
                    <img src={Icon} height={`${size}px`} width={`${size}px`} alt="" />
                ) : (
                    <Icon style={{ fontSize: '20px', color: 'grey' }} />
                )}
            </Tooltip>
        );
    }
    return <img src={Icon} height={`${size}px`} width={`${size}px`} alt="" />;
}
export function booleanWarningIcon(warning) {
    if (warning === true) {
        return <Warning color="secondary" />;
    }
}

export function createClusterCustomIcon(cluster) {
    const count = cluster.getChildCount();
    let size = 'LargeXL';

    if (count < 10) {
        size = 'Small';
    } else if (count >= 10 && count < 100) {
        size = 'Medium';
    } else if (count >= 100 && count < 1000) {
        size = 'Large';
    }
    const options = {
        cluster: `markerCluster${size}`
    };

    return L.divIcon({
        html: `<div>
          <span class="markerClusterLabel markerClusterLabel${size}">${count}</span>
        </div>`,
        className: `${options.cluster}`
    });
}
